// src/firebase/logsService.ts
import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  orderBy, 
  limit, 
  where, 
  Timestamp,
  serverTimestamp,
  onSnapshot,
  QuerySnapshot,
  DocumentData,
  deleteDoc,
  doc
} from "firebase/firestore";
import { db } from "./config";

// Types pour les logs
export interface Log {
  id: string;
  timestamp: string;
  user: string;
  visitPage: string;
  message: string;
  ip: string;
  action: string;
  status: 'success' | 'warning' | 'error' | 'info';
  createdAt: Timestamp;
}

export interface LogInput {
  user: string;
  visitPage: string;
  message: string;
  ip?: string;
  action: string;
  status?: 'success' | 'warning' | 'error' | 'info';
}

export interface LogStats {
  totalLogs: number;
  activeUsers: number;
  successRate: number;
  avgResponseTime: string;
}

// Collection reference
const LOGS_COLLECTION = 'logs';

/**
 * Ajouter un nouveau log
 */
export const addLog = async (logData: LogInput): Promise<string> => {
  try {
    const docRef = await addDoc(collection(db, LOGS_COLLECTION), {
      ...logData,
      ip: logData.ip || 'Unknown',
      status: logData.status || 'info',
      createdAt: serverTimestamp()
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Erreur lors de l\'ajout du log:', error);
    throw error;
  }
};

/**
 * Récupérer tous les logs avec pagination
 */
export const getLogs = async (limitCount: number = 50): Promise<Log[]> => {
  try {
    const q = query(
      collection(db, LOGS_COLLECTION),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    const logs: Log[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      logs.push({
        id: doc.id,
        timestamp: data.createdAt ? 
          data.createdAt.toDate().toLocaleString('fr-FR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          }).replace(',', '') : 
          new Date().toLocaleString('fr-FR').replace(',', ''),
        user: data.user || 'Unknown',
        visitPage: data.visitPage || '',
        message: data.message || '',
        ip: data.ip || 'Unknown',
        action: data.action || 'UNKNOWN',
        status: data.status || 'info',
        createdAt: data.createdAt
      });
    });
    
    return logs;
  } catch (error) {
    console.error('Erreur lors de la récupération des logs:', error);
    throw error;
  }
};

/**
 * Récupérer les logs en temps réel
 */
export const subscribeToLogs = (
  callback: (logs: Log[]) => void,
  limitCount: number = 50
) => {
  const q = query(
    collection(db, LOGS_COLLECTION),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );
  
  return onSnapshot(q, (querySnapshot: QuerySnapshot<DocumentData>) => {
    const logs: Log[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      logs.push({
        id: doc.id,
        timestamp: data.createdAt ? 
          data.createdAt.toDate().toLocaleString('fr-FR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          }).replace(',', '') : 
          new Date().toLocaleString('fr-FR').replace(',', ''),
        user: data.user || 'Unknown',
        visitPage: data.visitPage || '',
        message: data.message || '',
        ip: data.ip || 'Unknown',
        action: data.action || 'UNKNOWN',
        status: data.status || 'info',
        createdAt: data.createdAt
      });
    });
    
    callback(logs);
  });
};

/**
 * Récupérer les statistiques des logs
 */
export const getLogStats = async (): Promise<LogStats> => {
  try {
    // Récupérer tous les logs pour calculer les statistiques
    const allLogsQuery = query(collection(db, LOGS_COLLECTION));
    const allLogsSnapshot = await getDocs(allLogsQuery);
    
    const totalLogs = allLogsSnapshot.size;
    
    // Calculer les utilisateurs actifs (dernières 24h)
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    
    const recentLogsQuery = query(
      collection(db, LOGS_COLLECTION),
      where('createdAt', '>=', Timestamp.fromDate(yesterday))
    );
    const recentLogsSnapshot = await getDocs(recentLogsQuery);
    
    const activeUsers = new Set();
    let successCount = 0;
    
    recentLogsSnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.user) {
        activeUsers.add(data.user);
      }
      if (data.status === 'success') {
        successCount++;
      }
    });
    
    const successRate = recentLogsSnapshot.size > 0 ? 
      (successCount / recentLogsSnapshot.size) * 100 : 0;
    
    return {
      totalLogs,
      activeUsers: activeUsers.size,
      successRate: Math.round(successRate * 10) / 10,
      avgResponseTime: '1.2s' // Placeholder - pourrait être calculé à partir des logs
    };
  } catch (error) {
    console.error('Erreur lors du calcul des statistiques:', error);
    return {
      totalLogs: 0,
      activeUsers: 0,
      successRate: 0,
      avgResponseTime: '0s'
    };
  }
};

/**
 * Filtrer les logs par utilisateur
 */
export const getLogsByUser = async (userEmail: string): Promise<Log[]> => {
  try {
    const q = query(
      collection(db, LOGS_COLLECTION),
      where('user', '==', userEmail),
      orderBy('createdAt', 'desc'),
      limit(100)
    );
    
    const querySnapshot = await getDocs(q);
    const logs: Log[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      logs.push({
        id: doc.id,
        timestamp: data.createdAt ? 
          data.createdAt.toDate().toLocaleString('fr-FR').replace(',', '') : 
          new Date().toLocaleString('fr-FR').replace(',', ''),
        user: data.user || 'Unknown',
        visitPage: data.visitPage || '',
        message: data.message || '',
        ip: data.ip || 'Unknown',
        action: data.action || 'UNKNOWN',
        status: data.status || 'info',
        createdAt: data.createdAt
      });
    });
    
    return logs;
  } catch (error) {
    console.error('Erreur lors de la récupération des logs par utilisateur:', error);
    throw error;
  }
};

/**
 * Fonction utilitaire pour enregistrer une visite de page
 */
export const logPageVisit = async (userEmail: string, pageName: string, ip?: string) => {
  return addLog({
    user: userEmail,
    visitPage: pageName,
    message: `User visited page: ${pageName}`,
    action: 'PAGE_VISIT',
    status: 'success',
    ip
  });
};

/**
 * Fonction utilitaire pour enregistrer une connexion
 */
export const logUserLogin = async (userEmail: string, ip?: string) => {
  return addLog({
    user: userEmail,
    visitPage: 'LOGIN',
    message: `User logged in successfully`,
    action: 'LOGIN',
    status: 'success',
    ip
  });
};

/**
 * Fonction utilitaire pour enregistrer une tentative de connexion échouée
 */
export const logFailedLogin = async (userEmail: string, reason: string, ip?: string) => {
  return addLog({
    user: userEmail,
    visitPage: 'LOGIN',
    message: `Failed login attempt: ${reason}`,
    action: 'FAILED_LOGIN',
    status: 'error',
    ip
  });
};

/**
 * Fonction pour nettoyer les logs de démonstration
 */
export const cleanDemoLogs = async (): Promise<number> => {
  try {
    const demoEmails = [
      'admin@example.com',
      'user@example.com', 
      'test@example.com',
      'invalid@example.com'
    ];
    
    let deletedCount = 0;
    
    for (const email of demoEmails) {
      const q = query(
        collection(db, LOGS_COLLECTION),
        where('user', '==', email)
      );
      
      const querySnapshot = await getDocs(q);
      
      for (const docSnapshot of querySnapshot.docs) {
        await deleteDoc(doc(db, LOGS_COLLECTION, docSnapshot.id));
        deletedCount++;
      }
    }
    
    return deletedCount;
  } catch (error) {
    console.error('Erreur lors du nettoyage des logs de démonstration:', error);
    throw error;
  }
};